import { Link } from 'react-router'
import PageShell from '../components/PageShell'
import { STATES, STATE_CONTENT_REVIEW_DATE, type StateContent } from '../content/states'
import { APP_REGISTER_URL } from '../content/constants'

/** Which state-page section a task guide belongs to. Unknown slugs are not rendered. */
const TASK_GUIDE_SECTIONS: Record<string, 'registration' | 'certification' | 'prevailingWage'> = {
  'commbuys-vendor-registration': 'registration',
  'ctsource-contractor-registration': 'registration',
  'dcamm-contractor-certification': 'certification',
  'new-england-prevailing-wage': 'prevailingWage',
}

/** Shared layout for the six New England state pages. All prose comes from
 * the per-state content model — only the structure is shared. */
export default function StatePage({ state }: { state: StateContent }) {
  const others = STATES.filter((s) => s.slug !== state.slug)
  const resourceById = new Map(state.officialResources.map((r) => [r.id, r]))
  const taskGuidesFor = (section: 'registration' | 'certification' | 'prevailingWage') =>
    (state.taskGuides ?? []).filter((g) => TASK_GUIDE_SECTIONS[g.slug] === section)
  const renderTaskGuides = (section: 'registration' | 'certification' | 'prevailingWage') =>
    taskGuidesFor(section).map((g, index) => (
      <span key={g.slug}>
        {index > 0 && ' and '}
        <Link to={`/guides/${g.slug}`} className="text-[#06b6d4] hover:text-[#22d3ee]">
          {g.label}
        </Link>
      </span>
    ))

  return (
    <PageShell title={`Government Bids in ${state.name}`} intro={state.heroCopy}>
      <div className="mb-8 text-xs font-mono uppercase tracking-[0.06em] text-[#6b7280]">
        Official-source information last verified: {STATE_CONTENT_REVIEW_DATE}
      </div>

      {/* At a glance: concise, source-supported direct answers */}
      <section className="mb-12 bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 md:p-8">
        <h2 className="text-xl font-semibold text-white mb-5">
          {state.name} public bidding at a glance
        </h2>
        <dl className="space-y-5">
          {state.directAnswers.map((item) => (
            <div key={item.id}>
              <dt className="text-[15px] font-semibold text-white">{item.question}</dt>
              <dd className="mt-1 text-[15px] text-[#9ca3af] leading-[1.7]">
                {item.answer}
              </dd>
              <dd className="mt-1.5 text-sm leading-[1.6]">
                <span className="text-[#6b7280]">Sources: </span>
                {item.resourceIds.map((id, index) => {
                  const resource = resourceById.get(id)
                  if (!resource) return null
                  return (
                    <span key={id}>
                      {index > 0 && <span className="text-[#6b7280]"> · </span>}
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#06b6d4] hover:text-[#22d3ee]"
                      >
                        {resource.label} ↗
                      </a>
                    </span>
                  )
                })}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-6 text-sm text-[#6b7280] leading-[1.7]">
          Requirements vary by project type, agency, funding, and contract value —
          always confirm the solicitation and issuing agency requirements before
          bidding.
        </p>
      </section>

      {/* Where the state itself posts opportunities */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">
          Where can contractors find official {state.name} bid opportunities?
        </h2>
        {state.officialOpportunities.map((paragraph) => (
          <p key={paragraph} className="text-[15px] text-[#9ca3af] leading-[1.7] mb-4">
            {paragraph}
          </p>
        ))}
        <p className="text-[15px] text-[#9ca3af] leading-[1.7]">
          ProcurAlerts does not replace these official postings — the state
          portal and the issuing agency’s solicitation always control the
          requirements, documents, and deadline.
        </p>
      </section>

      {/* B. What ProcurAlerts monitors */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">
          What ProcurAlerts monitors in {state.name}
        </h2>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7] mb-4">
          ProcurAlerts tracks postings from public purchasing organizations across{' '}
          {state.name}, including:
        </p>
        <ul className="space-y-2 text-[15px] text-[#9ca3af] leading-[1.7] list-disc list-inside marker:text-[#6b7280] mb-4">
          {state.purchaserTypes.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7] mb-2">
          Examples of sources in the current coverage include:
        </p>
        <ul className="space-y-2 text-[15px] text-[#9ca3af] leading-[1.7] list-disc list-inside marker:text-[#6b7280]">
          {state.representativeSources.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-[#6b7280] leading-[1.6]">
          Coverage grows over time and no service can guarantee that every posting
          from every buyer is captured — always confirm opportunities with the
          issuing organization.
        </p>
      </section>

      {/* C. Opportunities contractors can find */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">
          Opportunities {state.name} contractors can find
        </h2>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7] mb-4">
          Public buyers in {state.name} regularly procure work and services such as:
        </p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 space-y-2 text-[15px] text-[#9ca3af] leading-[1.7] list-disc list-inside marker:text-[#6b7280]">
          {state.opportunityCategories.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </section>

      {/* Getting started checklist */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">
          How should contractors get started with {state.name} public bidding?
        </h2>
        <ol className="space-y-3 text-[15px] text-[#9ca3af] leading-[1.7] list-decimal list-inside marker:text-[#6b7280]">
          {state.gettingStarted.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>

      {/* Registration vs. licensing vs. prequalification */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">
          Do vendors need to register or prequalify before bidding in {state.name}?
        </h2>
        {state.registrationNotes.map((paragraph) => (
          <p key={paragraph} className="text-[15px] text-[#9ca3af] leading-[1.7] mb-4">
            {paragraph}
          </p>
        ))}
        <p className="text-[15px] text-[#9ca3af] leading-[1.7]">
          Our guide to{' '}
          <Link to="/guides/vendor-registration-vs-certification" className="text-[#06b6d4] hover:text-[#22d3ee]">
            vendor registration versus certification
          </Link>{' '}
          explains why these steps are not interchangeable.
        </p>
        {taskGuidesFor('registration').length > 0 && (
          <p className="mt-3 text-[15px] text-[#9ca3af] leading-[1.7]">
            For the step-by-step portal process, see {renderTaskGuides('registration')}.
          </p>
        )}
      </section>

      {/* Certifications and supplier diversity */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">
          Which certifications and supplier-diversity programs are available in {state.name}?
        </h2>
        {state.certificationNotes.map((paragraph) => (
          <p key={paragraph} className="text-[15px] text-[#9ca3af] leading-[1.7] mb-4">
            {paragraph}
          </p>
        ))}
        <p className="text-[15px] text-[#9ca3af] leading-[1.7]">
          See the guide to{' '}
          <Link to="/guides/government-contractor-certifications" className="text-[#06b6d4] hover:text-[#22d3ee]">
            government contractor certifications
          </Link>{' '}
          before paying for any credential.
        </p>
        {taskGuidesFor('certification').length > 0 && (
          <p className="mt-3 text-[15px] text-[#9ca3af] leading-[1.7]">
            For public building work in this state, review {renderTaskGuides('certification')} before bidding.
          </p>
        )}
      </section>

      {/* Prevailing wage / public works */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">
          Does {state.name} require prevailing wages on public projects?
        </h2>
        {state.prevailingWageNotes.map((paragraph) => (
          <p key={paragraph} className="text-[15px] text-[#9ca3af] leading-[1.7] mb-4">
            {paragraph}
          </p>
        ))}
        <p className="text-[15px] text-[#9ca3af] leading-[1.7]">
          The{' '}
          <Link to="/guides/prevailing-wage" className="text-[#06b6d4] hover:text-[#22d3ee]">
            prevailing-wage guide
          </Link>{' '}
          covers wage determinations, classifications, and certified payroll in
          more detail.
        </p>
        {taskGuidesFor('prevailingWage').length > 0 && (
          <p className="mt-3 text-[15px] text-[#9ca3af] leading-[1.7]">
            Comparing rules across the region? See {renderTaskGuides('prevailingWage')}, state by state.
          </p>
        )}
      </section>

      {/* D. How ProcurAlerts helps */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">How ProcurAlerts helps</h2>
        <ol className="space-y-3 text-[15px] text-[#9ca3af] leading-[1.7] list-decimal list-inside marker:text-[#6b7280] mb-4">
          <li>Select your trades and the {state.name} counties or towns you serve.</li>
          <li>Receive matched opportunities in a daily, deadline-sorted digest.</li>
          <li>Review deadlines and the source information captured for each bid.</li>
          <li>Track bids through your watchlist, from Watching to Won or Lost.</li>
          <li>Prepare your response with the Document Vault and Bid Package tools.</li>
        </ol>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7]">
          <strong className="text-white">Always verify:</strong> confirm deadlines,
          addenda, requirements, documents, and submission instructions with the
          issuing agency or the official procurement source before bidding. Learn
          more about <Link to="/features" className="text-[#06b6d4] hover:text-[#22d3ee]">ProcurAlerts features</Link> and{' '}
          <Link to="/how-it-works" className="text-[#06b6d4] hover:text-[#22d3ee]">how the workflow fits together</Link>.
        </p>
      </section>

      {/* E. State-specific value */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">
          Why {state.name} contractors use ProcurAlerts
        </h2>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7]">{state.stateValueCopy}</p>
      </section>

      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">New to public contracting?</h2>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7] mb-4">
          Build a repeatable process before pursuing your first {state.name} opportunity — our{' '}
          <Link to="/guides" className="text-[#06b6d4] hover:text-[#22d3ee]">government contracting guides</Link>{' '}
          are a good starting point:
        </p>
        <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <li><Link to="/guides/public-procurement" className="block h-full bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 text-sm font-semibold text-white hover:border-[#06b6d4]/40">Understand public procurement</Link></li>
          <li><Link to="/guides/how-to-bid-on-government-contracts" className="block h-full bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 text-sm font-semibold text-white hover:border-[#06b6d4]/40">Learn how to prepare a bid</Link></li>
          <li><Link to="/guides/government-bid-checklist" className="block h-full bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 text-sm font-semibold text-white hover:border-[#06b6d4]/40">Use the submission checklist</Link></li>
        </ul>
      </section>

      {/* F. CTA */}
      <section className="mb-12 bg-white/[0.03] border border-white/[0.06] rounded-xl p-8">
        <h2 className="text-xl font-semibold text-white mb-3">
          Start finding {state.name} bids
        </h2>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7] mb-6">
          Try ProcurAlerts free for 14 days — set your trades and territory in
          minutes, or browse the current {state.name} inventory first. See{' '}
          <Link to="/pricing" className="text-[#06b6d4] hover:text-[#22d3ee]">pricing</Link> for plan details.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href={APP_REGISTER_URL}
            className="inline-block text-center bg-[#06b6d4] text-[#0d1117] text-sm font-semibold px-6 py-3 rounded-lg hover:bg-[#22d3ee] transition-all duration-200"
          >
            Start your 14-day free trial
          </a>
          <a
            href={state.browseUrl}
            className="inline-block text-center border border-white/20 text-white text-sm font-semibold px-6 py-3 rounded-lg hover:border-[#06b6d4] hover:text-[#06b6d4] transition-all duration-200"
          >
            {state.browseLabel}
          </a>
        </div>
      </section>

      {/* Official government resources */}
      <section className="mb-12 border-t border-white/[0.08] pt-8">
        <h2 className="text-xl font-semibold text-white mb-3">
          Official {state.name} resources
        </h2>
        <ul className="space-y-2">
          {state.officialResources.map((resource) => (
            <li key={resource.url}>
              <a
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#06b6d4] hover:text-[#22d3ee]"
              >
                {resource.label} ↗
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-5 text-sm text-[#6b7280] leading-[1.7]">
          Links verified {STATE_CONTENT_REVIEW_DATE}. Program names, thresholds,
          and requirements change — rely on the current official page and
          solicitation.
        </p>
      </section>

      {/* Independence notice */}
      <aside className="mb-12 border-l-2 border-[#06b6d4] bg-[#06b6d4]/[0.05] p-5 text-sm text-[#9ca3af] leading-[1.7]">
        ProcurAlerts is an independent, private service. It is not a government
        agency and is not affiliated with or endorsed by the State of{' '}
        {state.name} or any office, portal, or program mentioned on this page.
        Contractors must verify all requirements and deadlines in the official
        solicitation and with the issuing organization before bidding. Nothing
        on this page is legal advice.
      </aside>

      {/* Explore other states */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-3">
          Explore another New England state
        </h2>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7] mb-4">
          ProcurAlerts covers all six New England states. See the{' '}
          <Link to="/government-bids" className="text-[#06b6d4] hover:text-[#22d3ee]">New England government bids hub</Link>, the{' '}
          <Link to="/coverage" className="text-[#06b6d4] hover:text-[#22d3ee]">regional coverage overview</Link> or
          pick another state:
        </p>
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {others.map((s) => (
            <li key={s.slug}>
              <Link
                to={`/government-bids/${s.slug}`}
                className="block bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 hover:border-[#06b6d4]/40 transition-colors"
              >
                <span className="text-xs font-mono uppercase tracking-[0.08em] text-[#06b6d4]">
                  {s.abbreviation}
                </span>
                <span className="mt-1 block text-base font-semibold text-white">{s.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </PageShell>
  )
}
